// @ts-strict-ignore
// eslint-disable-next-line max-classes-per-file
import React, { useEffect } from 'react';
import { func } from 'prop-types';
import {
  View,
  TouchableHighlight,
  GestureResponderEvent,
  Pressable,
} from 'react-native';
import Swiper, { SwiperControlsProps } from 'react-native-web-swiper';
import {
  Box,
  IBoxProps,
  Inline,
} from '@styleseat/ui';
import { colors, toRGBA as rgba } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import {
  BodyText,
  Stack,
  Absolute,
  Skeleton,
} from '../../../ui';

import Image from '../../Image';
import { useResponsiveEffect } from '../../../../hooks';

import './ImageSwiper.module.scss';
import { ChevronIcon } from '../../../ui/Icons';
import { IBasicImage } from '../../../../api/Providers/GalleryImages';

type PagingComponentProps = {
  onPress: () => void;
  testID?: string;
};

const AnyImage = Image as any;

const flexStyle: IBoxProps['style'] = {
  flex: 1,
};

const PagingComponent: React.FCWithChildren<PagingComponentProps> = ({
  onPress,
  children,
  testID,
}) => (
  <TouchableHighlight
    onPress={onPress}
    style={{
      backgroundColor: rgba(colors.blackInBlack, 0.8),
      borderRadius: 10,
      padding: 4,
    }}
    testID={testID}
  >
    <View>
      {children}
    </View>
  </TouchableHighlight>
);

// eslint-disable-next-line react/prefer-stateless-function
class EmptyComponent extends React.Component<any> {
  render() {
    return null;
  }
}

// this is needed to provide type safety with the Swiper, which would ideally support function
// components
// eslint-disable-next-line react/prefer-stateless-function
class PrevComponent extends React.Component<{ onPress: () => void }> {
  static propTypes = {
    onPress: func.isRequired,
  };

  render() {
    return (
      <PagingComponent
        onPress={this.props.onPress}
        testID="gallery-image-modal-previous"
      >
        <ChevronIcon direction="left" color={colors.whiteWash} />
      </PagingComponent>
    );
  }
}

// eslint-disable-next-line react/prefer-stateless-function
class NextComponent extends React.Component<{ onPress: () => void }> {
  static propTypes = {
    onPress: func.isRequired,
  };

  render() {
    return (
      <PagingComponent
        onPress={this.props.onPress}
        testID="gallery-image-modal-next"
      >
        <ChevronIcon direction="right" color={colors.whiteWash} />
      </PagingComponent>
    );
  }
}

const swiperControlsProps: SwiperControlsProps = {
  dotsTouchable: true,
  dotProps: {
    badgeStyle: {
      backgroundColor: colors.lightGrey,
    },
  },
  dotActiveStyle: {
    backgroundColor: colors.blackInBlack,
  },
  prevPos: 'left',
  nextPos: 'right',
  // hide prev/next buttons
  DotComponent: EmptyComponent,
  PrevComponent,
  NextComponent,
};

export type ImageSwiperProps = {
  /** The index of the image to display when the modal is opened */
  initialIndex?: number;
  /** The set of images to display in the carousel */
  images?: Array<IBasicImage>;
  /** imageTotalCount */
  imageTotal?: number;
  /** Any additional components to display immediately below the image carousel */
  children?: React.ReactNode;
  /** Transforms the given image URL into an absolute URL */
  formatImageUrl: (image: { url: string }) => string;
  /** On index changed, when the user moves to the next index */
  onIndexChanged?: (index: number) => void;
  /** What happens when the animation ends */
  onAnimationEnd?: (index: number) => void;
  /** Called when a gallery image is clicked */
  onImagePress?: (index: number) => void;
  /** changes to this key trigger a re-render of the swiper */
  swiperKey?: string;
  /** Make images slightly smaller on desktop modal to make room for info */
  imageSize?: 'small';
  /** Determines whether to show the current over total image count */
  hideImageCounter?: boolean;
  /** Whether to show the prev/next buttons on mobile screen */
  showPagingButtonsOnMobile?: boolean;
  /** Whether the image swiper should loop from last to first */
  loop?: boolean;
  /** Parent context to send with analytics events */
  trackingParams?: Record<string, any> & { source: string };
  /** Maximum height of swiper */
  maxHeight?: number | `${number}%`;
  /** Test ID prefix */
  testIdPrefix?: string;
};

export type SubsetOfFieldsFromImageSwiper<Y extends keyof ImageSwiperProps> = (
  Pick<ImageSwiperProps, Y>
);

const ImageSwiper: React.FCWithChildren<ImageSwiperProps> = ({
  initialIndex = 0,
  images,
  imageTotal,
  children,
  formatImageUrl,
  onIndexChanged,
  onAnimationEnd,
  onImagePress,
  swiperKey,
  imageSize,
  hideImageCounter = false,
  showPagingButtonsOnMobile = false,
  loop = true,
  trackingParams = {
    source: 'general',
  },
  maxHeight,
  testIdPrefix = '',
}) => {
  const [forceSwiperUpdate, setForceSwiperUpdate] = React.useState<boolean>(false);
  const [index, setIndex] = React.useState<number>(initialIndex);
  const is = useResponsiveEffect();

  const isMobile = is.mobile || is.tablet;
  const isDesktopWide = is.desktop && !is.desktopNarrow;

  const swiperProps = { ...swiperControlsProps };

  if (!showPagingButtonsOnMobile && isMobile) {
    swiperProps.NextComponent = EmptyComponent;
    swiperProps.PrevComponent = EmptyComponent;
  }

  const handleIndexChanged = React.useCallback((newIndex: number) => {
    analytics.track(`client_${trackingParams?.source}_gallery_image_swiped`, {
      index: newIndex,
      ...trackingParams,
    });

    setIndex(newIndex);

    if (onIndexChanged) {
      onIndexChanged(newIndex);
    }
  }, [
    onIndexChanged,
    trackingParams,
  ]);

  /**
   * The following two effects are used to hack a rerender of the Swiper component
   * since the third party library doesn't rerender when children are updated.
   * We need this to support pagination.
   */
  useEffect(() => {
    setForceSwiperUpdate(true);
  }, [images.length]);

  useEffect(() => {
    if (forceSwiperUpdate) {
      setForceSwiperUpdate(false);
    }
  }, [forceSwiperUpdate]);

  const handleImagePress = (e: GestureResponderEvent) => {
    analytics.track(`client_${trackingParams?.source}_gallery_image_clicked`, {
      index,
      ...trackingParams,
    });

    if (onImagePress) {
      onImagePress(index);
    }

    e.preventDefault();
  };

  return (
    <View style={{ flex: 1 }}>
      <Stack style={{ flex: 1 }} alignItems="center">
        <View
          styleName={imageSize === 'small' ? 'gallery-image-modal__swiper-wrapper-smaller' : 'gallery-image-modal__swiper-wrapper'}
          style={{
            maxHeight: maxHeight || undefined,
          }}
        >
          {forceSwiperUpdate ? null : (
            <Swiper
              controlsProps={swiperProps}
              from={index}
              loop={loop}
              onIndexChanged={handleIndexChanged}
              onAnimationEnd={onAnimationEnd}
              key={swiperKey}
            >
              {images.map(img => (
                <Inline
                  key={img.id}
                  justifyContent="center"
                  style={{
                    height: '100%',
                  }}
                  paddingX={isDesktopWide ? undefined : 12}
                >
                  <Pressable
                    onPress={handleImagePress}
                    style={flexStyle}
                  >
                    <AnyImage
                      testID={`${testIdPrefix.length ? `${testIdPrefix}-` : ''}gallery-image-modal-image`}
                      source={formatImageUrl(img)}
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                      accessibilityLabel="image"
                      // always show full image on desktop
                      resizeMode={isDesktopWide ? 'contain' : undefined}
                      resizeMethod={isDesktopWide ? 'scale' : undefined}
                    />
                  </Pressable>
                </Inline>
              ))}
              {/* Supporting pagination means adding an extra element that
              tells us it's time to load more.
              Fetching the next page is handled by consumer.
              */}
              {imageTotal > images.length && (
                <Box flex={1}>
                  <Skeleton
                    width="100%"
                    height="360px"
                    widthRandomness={0}
                  />
                </Box>
              )}
            </Swiper>
          )}
          {!hideImageCounter && (
            <Absolute top={8} left={0} right={0}>
              <Inline
                justifyContent="space-around"
              >
                <Box
                  color={colors.blackInBlack}
                  borderRadius={50}
                  paddingX={8}
                >
                  <BodyText color={colors.whiteWash}>
                    {index + 1}/{imageTotal || images.length}
                  </BodyText>
                </Box>
              </Inline>
            </Absolute>
          )}
        </View>
        {children}
      </Stack>
    </View>
  );
};

export default ImageSwiper;
